/**
 * Freight acknowledgement gate.
 *
 * Freight is billed C.O.D. by R+L, so checkout shows no shipping charge. The
 * customer has to confirm they understand that before they can pay, and the
 * answer is written to the cart as an attribute so it appears on the order.
 *
 * The gate is armed by this script, never by the markup: if the script fails
 * to load, checkout stays usable rather than becoming impossible.
 *
 * The cart page and the drawer each render their own copy of the summary, so
 * every checkbox is kept in step with the others.
 */
const ATTRIBUTE = 'Freight acknowledged';
const GROUP = '[data-za-ack-group]';
const INPUT = '[data-za-ack-input]';

const inputs = () => Array.from(document.querySelectorAll(INPUT));

/** The cart is acknowledged when any rendered checkbox is ticked. */
const isAcknowledged = () => inputs().some((input) => input.checked);

/** Enables or disables checkout, and hides the accelerated buttons with it. */
function applyState() {
  const acknowledged = isAcknowledged();

  for (const input of inputs()) {
    if (input.checked !== acknowledged) input.checked = acknowledged;
  }

  for (const group of document.querySelectorAll(GROUP)) {
    // An empty cart has no checkbox and keeps Shopify's own disabled state.
    if (!group.querySelector(INPUT)) continue;

    group.dataset.zaAck = acknowledged ? 'ok' : 'pending';

    const checkout = group.querySelector('.cart__checkout-button');
    if (checkout) {
      checkout.disabled = !acknowledged;
      checkout.setAttribute('aria-disabled', String(!acknowledged));
    }
  }
}

/** Stores the answer on the cart, so it reaches the order in admin. */
async function persist(acknowledged) {
  try {
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [ATTRIBUTE]: acknowledged ? 'Yes' : '' } }),
    });
  } catch (error) {
    // A failed write must not strand the customer: the checkbox also posts
    // with the cart form, which carries the attribute through on checkout.
    console.warn('[freight-ack] Could not store the acknowledgement:', error);
  }
}

document.addEventListener('change', (event) => {
  const input = event.target instanceof Element ? event.target.closest(INPUT) : null;
  if (!input) return;

  applyState();
  persist(input.checked);
});

/** Points at the checkbox when someone tries to check out without ticking it. */
document.addEventListener('click', (event) => {
  const checkout = event.target instanceof Element ? event.target.closest('.cart__checkout-button') : null;
  if (!checkout || !checkout.disabled) return;

  const group = checkout.closest(GROUP);
  const row = group?.querySelector('.za-freight-ack');
  const input = group?.querySelector(INPUT);
  if (!row || !input) return;

  row.classList.add('za-freight-ack--missing');
  input.focus();
});

applyState();

// The cart and the drawer re-render on every quantity change, which replaces
// these nodes, so the gate is re-applied whenever the subtree changes. The
// work is coalesced into one frame so unrelated page animations stay cheap.
let queued = false;
const observer = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    applyState();
  });
});
observer.observe(document.body, { childList: true, subtree: true });
